import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import moment from 'moment';
import { Logger } from '../logger.service';

const log = new Logger('HttpCacheService');
const cachePersistenceKey = 'httpCache';

export interface HttpCacheEntry {
  lastUpdated: Date;
  expirationDate: Date;
  data: HttpResponse<any>;
}

/**
 * Provides a cache facility for HTTP requests with configurable persistence policy.
 */
@Injectable()
export class HttpCacheService {
  // -----------------------------------------------------------------------------------------------------
  // @ PRIVATE INSTANCE VARIABLES
  // -----------------------------------------------------------------------------------------------------

  private cachedData: { [key: string]: HttpCacheEntry } = {};
  private storage: Storage | null = null;

  // -----------------------------------------------------------------------------------------------------
  // @ CONSTRUCTOR
  // -----------------------------------------------------------------------------------------------------

  constructor() {
    this.cleanCache(null, true);
    this.loadCacheData();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ PUBLIC METHODS
  // -----------------------------------------------------------------------------------------------------

  /**
   * Sets the cache data for the specified request.
   * @param url The request URL.
   * @param data The received data.
   * @param lastUpdated The cache last update, current date is used if not specified.
   */
  setCacheData(url: string, data: HttpResponse<any>, lastUpdated?: Date, expirationDate?: Date) {
    this.cachedData[url] = {
      lastUpdated: lastUpdated || new Date(),
      expirationDate:
        expirationDate ||
        moment()
          .add(60 * 24, 'minutes') // 1 day
          .toDate(),
      data
    };
    log.debug(`Cache set for key: "${url}" until ${expirationDate}`);
    this.saveCacheData();
  }

  /**
   * Gets the cached data for the specified request.
   * @param url The request URL.
   * @return The cached data or null if no cached data exists for this request.
   */
  getCacheData(url: string): HttpResponse<any> | null {
    const cacheEntry = this.cachedData[url];

    if (cacheEntry) {
      if (moment() > moment(cacheEntry.expirationDate)) {
        this.clearCache(url);
        return null;
      }

      log.debug(`Cache hit for key: "${url}"`);
      return cacheEntry.data;
    }

    return null;
  }

  /**
   * Gets the cached entry for the specified request.
   * @param url The request URL.
   * @return The cache entry or null if no cache entry exists for this request.
   */
  getHttpCacheEntry(url: string): HttpCacheEntry | null {
    return this.cachedData[url] || null;
  }

  /**
   * Clears the cached entry (if exists) for the specified request.
   * @param  url The request URL.
   */
  clearCache(url: string): void {
    delete this.cachedData[url];
    log.debug(`Cache cleared for key: "${url}"`);
    this.saveCacheData();
  }

  /**
   * Cleans cache entries older than the specified date.
   * @param expiration The cache expiration date. If no date is specified, all cache is cleared.
   */
  cleanCache(expiration?: Date, removeExpired: boolean = false) {
    if (expiration) {
      /*each(this.cachedData, (value: HttpCacheEntry, key: string) => {
        if (expirationDate >= value.lastUpdated) {
          delete this.cachedData[key];
        }
      });*/
    } else {
      if (removeExpired === true) {
        for (let index = 0; index < Object.keys(this.cachedData).length; index++) {
          const element: HttpCacheEntry = this.cachedData[index];
          if (element.expirationDate <= new Date()) {
            delete this.cachedData[index];
          }
        }
      } else {
        this.cachedData = {};
      }
    }
    this.saveCacheData();
  }

  /**
   * Sets the cache persistence policy.
   * Note that changing the cache persistence will also clear the cache from its previous storage.
   * @param persistence How the cache should be persisted, it can be either local or session
   *   storage, or if no value is provided it will be only in-memory (default).
   */
  setPersistence(persistence?: 'local' | 'session') {
    // this.cleanCache();
    this.storage = persistence === 'local' || persistence === 'session' ? (window as any)[persistence + 'Storage'] : null;
    this.loadCacheData();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ PRIVATE METHODS
  // -----------------------------------------------------------------------------------------------------

  private saveCacheData() {
    if (this.storage) {
      this.storage[cachePersistenceKey] = JSON.stringify(this.cachedData);
    }
  }

  private loadCacheData() {
    const data = this.storage ? this.storage[cachePersistenceKey] : null;
    this.cachedData = data ? JSON.parse(data) : {};
  }

}
